import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Text,
  TouchableOpacity,
  View,
  Pressable,
  Platform,
} from 'react-native';
import Icon from '../Icon/Icon';
import { useColors } from '../../hooks/useColors';
import Sidebar from '../Sidebar/Sidebar';
import ActionPanelStyles from './ActionPanel.styles';
import { useTranslation } from '@oolio-group/localization';
import {
  selectedDocketVar,
  bulkSelectVar,
  rushedVar,
} from '../../state/apolloVars';
import { useReactiveVar } from '@apollo/client';
import { TAGS, DocketPlacement } from '../../common/constants';
import { sendNotification } from '../../hooks/useNotification';
import Tooltip from '../Tooltip/Tooltip';
import { DocketStatus, NotifierDocketItems } from '@oolio-group/domain';
import { debounce } from 'lodash';

interface ActionPanelProps {
  visible: boolean;
  onClose: () => void;
  tagDockets: (
    status: DocketStatus,
    ids: string[],
    tags: string[],
    clearExistingTags?: boolean,
  ) => Promise<{ error: string }>;
  untagDockets: (
    status: DocketStatus,
    ids: string[],
    tags: string[],
  ) => Promise<{ error: string }>;
  printKitchenDocket?: (
    docket: string,
    status: DocketStatus,
    notifiers?: NotifierDocketItems[],
  ) => Promise<void>;
  rushDockets: (ids: string[]) => Promise<{ error: string }>;
  unrushDockets: (ids: string[]) => Promise<{ error: string }>;
  selectedTab: DocketStatus;
  onMoveDocket: (placement: DocketPlacement) => void;
  enablePrint?: boolean;
}

const ActionPanel: React.FC<ActionPanelProps> = ({
  visible,
  onClose,
  tagDockets,
  untagDockets,
  rushDockets,
  unrushDockets,
  printKitchenDocket,
  selectedTab,
  onMoveDocket,
  enablePrint = false,
}: ActionPanelProps) => {
  const styles = ActionPanelStyles();
  const { translate } = useTranslation();
  const { colors } = useColors();
  const selectedDockets = useReactiveVar(selectedDocketVar);
  const isBulkMode = useReactiveVar(bulkSelectVar);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [hoveredTag, setHoveredTag] = useState('');

  useEffect(() => {
    if (!visible) {
      setSelectedTags([]);
      return;
    }
    if (!isBulkMode) {
      const docket = Object.values(selectedDockets)[0];
      setSelectedTags(docket?.tags || []);
    }
  }, [visible, selectedDockets, isBulkMode]);

  const handlePressTag = useCallback(
    async (tag: string) => {
      if (isBulkMode) {
        setSelectedTags(prevTags =>
          prevTags.includes(tag)
            ? prevTags.filter(t => t !== tag)
            : [...prevTags, tag],
        );
        return;
      }

      const docket = Object.values(selectedDockets)[0];
      if (!docket) return;

      const hasTag = docket.tags?.includes(tag);
      const action = hasTag ? untagDockets : tagDockets;
      const resp = await action(selectedTab, [docket.id], [tag]);
      if (resp.error) {
        sendNotification({
          error: true,
          message: resp.error,
        });
      }
    },
    [
      tagDockets,
      untagDockets,
      setSelectedTags,
      selectedDockets,
      isBulkMode,
      selectedTab,
    ],
  );

  const handleReposition = useMemo(
    () => debounce(onMoveDocket, 150),
    [onMoveDocket],
  );

  const handleApplyTags = useCallback(async () => {
    const resp = await tagDockets(
      selectedTab,
      Object.keys(selectedDockets),
      selectedTags,
      true,
    );
    if (resp.error) {
      sendNotification({
        error: true,
        message: resp.error,
      });
      return;
    }
    onClose();
  }, [tagDockets, selectedDockets, selectedTags, onClose, selectedTab]);

  const handlePrintKitchenDocket = useCallback(() => {
    const docket = Object.values(selectedDockets)?.[0];
    if (docket) {
      printKitchenDocket?.(docket.id, docket.status, docket.notifierItems);
    }
  }, [printKitchenDocket, selectedDockets]);

  const handleRushDockets = useCallback(async () => {
    const ids = Object.keys(selectedDockets);
    const resp = await rushDockets(ids);
    if (resp.error) {
      sendNotification({
        error: true,
        message: resp.error,
      });
      return;
    }
    rushedVar(ids.join('-') + 'rush');
    onClose();
  }, [rushDockets, selectedDockets, onClose]);

  const handleClearRushDockets = useCallback(async () => {
    const ids = Object.keys(selectedDockets);
    const resp = await unrushDockets(ids);
    if (resp.error) {
      sendNotification({
        error: true,
        message: resp.error,
      });
      return;
    }
    rushedVar(ids.join('-') + 'clear-rush');
    onClose();
  }, [unrushDockets, selectedDockets, onClose]);

  const title = useMemo(() => {
    const docketCount = Object.keys(selectedDockets).length;

    if (docketCount === 0) return translate('kitchenDisplay.selectDockets');
    if (docketCount === 1) {
      const { orderNumber, table, customer, section } =
        Object.values(selectedDockets)[0];
      const suffix = `${section} ${table}` || customer;
      return suffix.trim() ? `${orderNumber}  ${suffix}` : `${orderNumber}`;
    }
    return `${docketCount} ${translate('kitchenDisplay.dockets')}`;
  }, [selectedDockets, translate]);

  const renderMoveButtons = useMemo(() => {
    return (
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>
          {translate('kitchenDisplay.move')}
        </Text>
        <View style={styles.moveBtnContainer}>
          <TouchableOpacity
            style={styles.moveBtn}
            onPress={() => handleReposition(DocketPlacement.START)}
          >
            <Icon
              name="arrow-to-right"
              style={{ transform: [{ rotate: '180deg' }] }}
              size={24}
              color={colors.text}
            />
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.moveBtn}
            onPress={() => handleReposition(DocketPlacement.BACKWARD)}
          >
            <Icon name="arrow-left" size={24} color={colors.text} />
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.moveBtn}
            onPress={() => handleReposition(DocketPlacement.FORWARD)}
          >
            <Icon name="arrow-right" size={24} color={colors.text} />
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.moveBtn}
            onPress={() => handleReposition(DocketPlacement.END)}
          >
            <Icon name="arrow-to-right" size={24} color={colors.text} />
          </TouchableOpacity>
        </View>
      </View>
    );
  }, [styles, translate, colors.text, handleReposition]);

  const renderActions = useMemo(() => {
    const selectedDocket = Object.values(selectedDockets)[0];
    const isRushed = selectedDocket?.rushedAt;
    let rushLabel = translate('kitchenDisplay.rushDocket');
    let rushHandler = handleRushDockets;

    if (!isBulkMode && isRushed) {
      rushLabel = translate('kitchenDisplay.clearRush');
      rushHandler = handleClearRushDockets;
    }

    return (
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>
          {translate('kitchenDisplay.actions')}
        </Text>
        {selectedTab === DocketStatus.CREATED && (
          <TouchableOpacity
            style={styles.actionBtn}
            onPress={rushHandler}
            disabled={!selectedDocket}
          >
            <Text style={styles.actionText}>{rushLabel}</Text>
            <Icon name="angle-right" size={20} color={colors.grey5} />
          </TouchableOpacity>
        )}

        {!isBulkMode && enablePrint && (
          <TouchableOpacity
            style={styles.actionBtn}
            onPress={handlePrintKitchenDocket}
            disabled={isBulkMode}
          >
            <Text style={styles.actionText}>
              {translate('kitchenDisplay.printDocket')}
            </Text>
            <Icon name="angle-right" size={20} color={colors.grey5} />
          </TouchableOpacity>
        )}
      </View>
    );
  }, [
    styles,
    colors,
    handlePrintKitchenDocket,
    isBulkMode,
    translate,
    handleRushDockets,
    handleClearRushDockets,
    selectedDockets,
    selectedTab,
    enablePrint,
  ]);

  const renderTags = useMemo(() => {
    return (
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>
          {translate('kitchenDisplay.stickers')}
        </Text>
        <View style={styles.tagOptionsContainer}>
          {TAGS.map(({ tag, label }) => {
            const isSelected = selectedTags.includes(tag);
            return (
              <Pressable
                key={tag}
                onPress={() => handlePressTag(tag)}
                {...(Platform.OS === 'web' && {
                  onHoverIn: () => setHoveredTag(tag),
                  onHoverOut: () => setHoveredTag(''),
                })}
                onPressIn={() => setHoveredTag(tag)}
                onPressOut={() => setHoveredTag('')}
                style={[
                  styles.tagOption,
                  isSelected && styles.tagOptionSelected,
                ]}
              >
                <Text style={styles.tagOptionLabel}>{tag}</Text>
                {hoveredTag === tag && <Tooltip label={label} />}
              </Pressable>
            );
          })}
        </View>
        {selectedTags.length &&
        Object.keys(selectedDockets).length &&
        isBulkMode ? (
          <TouchableOpacity
            style={[styles.actionBtn, styles.tagApplyBtn]}
            onPress={handleApplyTags}
          >
            <Text style={[styles.actionText, styles.tagApplyBtnText]}>
              {translate('kitchenDisplay.applyStickers')}
            </Text>
          </TouchableOpacity>
        ) : null}
      </View>
    );
  }, [
    styles,
    handlePressTag,
    selectedTags,
    translate,
    hoveredTag,
    handleApplyTags,
    selectedDockets,
    isBulkMode,
  ]);

  if (!visible) return null;

  return (
    <Sidebar onClose={onClose} title={title} placement="right">
      {selectedTab === DocketStatus.CREATED && !isBulkMode && renderMoveButtons}
      {(selectedTab === DocketStatus.CREATED || !isBulkMode) && renderActions}
      {selectedTab === DocketStatus.CREATED && renderTags}
    </Sidebar>
  );
};

export default ActionPanel;
